import { DataItem, getDataClass } from 'scrivito'

import { DataClassName } from '@/Data/const'

/**
 * Maps each entity type to all DataClasses that must be invalidated when it changes.
 *
 * Rule: each entry reflects data ownership only — what belongs to this entity.
 * Workflow sequencing (e.g. "job finishes → list changes") is expressed at the call site
 * by composing two registry calls, not here.
 *
 * JOB owns only itself — it is a short-lived status object.
 * LIST owns LIST + CONTACT_LIST_RELATION — list membership belongs to the list.
 * Call invalidateViaRegistry(LIST) after a job that mutated list membership
 * completes; poll the job to completion first with waitFor() (src/utils/waitFor.ts).
 *
 * When a new relation DataClass is added: update this map.
 * When a tab shows stale data after a mutation: check this map first.
 *
 * Scrivito's write counter is per-DataClass. Mutating Activity only invalidates
 * the Activity cache — not ActivityContactRelation, which backs the Activities tab
 * on Company/Person detail pages. This registry ensures all affected caches refresh.
 */
const INVALIDATION_MAP: Readonly<
  Partial<Record<DataClassName, DataClassName[]>>
> = {
  [DataClassName.ACTIVITY]: [
    DataClassName.ACTIVITY,
    DataClassName.ACTIVITY_CONTACT_RELATION,
    DataClassName.ACTIVITY_PROJECT_RELATION,
    DataClassName.ACTIVITY_DOCUMENT_RELATION,
    DataClassName.ACTIVITY_FOLLOW_UP_RELATION,
  ],

  [DataClassName.COMPANY]: [
    DataClassName.COMPANY,
    DataClassName.COMPANY_PERSON_RELATION,
    DataClassName.CONTACT_CONTACT_RELATION,
    DataClassName.CONTACT_LIST_RELATION,
    DataClassName.CONTACT_PROJECT_RELATION,
    DataClassName.ADDRESS_CONTACT_RELATION,
    DataClassName.CONTACT_DOCUMENT_RELATION,
    DataClassName.ACTIVITY_CONTACT_RELATION,
  ],

  [DataClassName.PERSON]: [
    DataClassName.PERSON,
    DataClassName.COMPANY_PERSON_RELATION,
    DataClassName.CONTACT_CONTACT_RELATION,
    DataClassName.CONTACT_LIST_RELATION,
    DataClassName.CONTACT_PROJECT_RELATION,
    DataClassName.ADDRESS_CONTACT_RELATION,
    DataClassName.CONTACT_DOCUMENT_RELATION,
    DataClassName.ACTIVITY_CONTACT_RELATION,
  ],

  [DataClassName.TICKET]: [
    DataClassName.TICKET,
    DataClassName.CONTACT_PROJECT_RELATION,
    DataClassName.ACTIVITY_PROJECT_RELATION,
    DataClassName.DOCUMENT_PROJECT_RELATION,
  ],

  [DataClassName.OPPORTUNITY]: [
    DataClassName.OPPORTUNITY,
    DataClassName.CONTACT_PROJECT_RELATION,
    DataClassName.ACTIVITY_PROJECT_RELATION,
    DataClassName.DOCUMENT_PROJECT_RELATION,
  ],

  [DataClassName.ORDER]: [
    DataClassName.ORDER,
    DataClassName.CONTACT_PROJECT_RELATION,
    DataClassName.ACTIVITY_PROJECT_RELATION,
    DataClassName.DOCUMENT_PROJECT_RELATION,
  ],

  [DataClassName.QUOTE]: [
    DataClassName.QUOTE,
    DataClassName.CONTACT_PROJECT_RELATION,
    DataClassName.ACTIVITY_PROJECT_RELATION,
    DataClassName.DOCUMENT_PROJECT_RELATION,
  ],

  [DataClassName.MESSAGE]: [DataClassName.MESSAGE],

  [DataClassName.INVOICE]: [DataClassName.INVOICE],

  [DataClassName.LIST]: [
    DataClassName.LIST,
    DataClassName.CONTACT_LIST_RELATION,
  ],

  [DataClassName.JOB]: [DataClassName.JOB],

  [DataClassName.SOCIAL_NETWORK]: [DataClassName.SOCIAL_NETWORK],

  [DataClassName.DOCUMENT]: [
    DataClassName.DOCUMENT,
    DataClassName.ACTIVITY_DOCUMENT_RELATION,
    DataClassName.CONTACT_DOCUMENT_RELATION,
    DataClassName.DOCUMENT_DOCUMENT_RELATION,
    DataClassName.DOCUMENT_PROJECT_RELATION,
  ],

  [DataClassName.ADDRESS]: [
    DataClassName.ADDRESS,
    DataClassName.ADDRESS_CONTACT_RELATION,
  ],

  [DataClassName.FOLLOW_UP]: [
    DataClassName.FOLLOW_UP,
    DataClassName.ACTIVITY_FOLLOW_UP_RELATION,
  ],

  // Timerecord writes invalidate the record class plus the two derived
  // aggregates recomputed from logged hours: TIMERECORD_UTILIZATION (per user +
  // date, see useMyTeamData.ts) and TIMERECORD_BUDGET (consumed budget per order
  // + date, see useMyOrdersData.ts). The remaining Timerecord* classes (TEAM,
  // USER, CLIENT, ORDER, POSITION) are dimension/lookup tables that do not change
  // when time is logged, so they are intentionally excluded.
  [DataClassName.TIMERECORD]: [
    DataClassName.TIMERECORD,
    DataClassName.TIMERECORD_UTILIZATION,
    DataClassName.TIMERECORD_BUDGET,
  ],
}

/**
 * Returns all DataClasses that must be invalidated when the given entity type changes.
 * Falls back to [entityType] if no entry exists in the map.
 */
export function getInvalidationTargets(
  entityType: DataClassName,
): DataClassName[] {
  return INVALIDATION_MAP[entityType] ?? [entityType]
}

/**
 * Invalidates all DataClass caches related to the given entity type.
 *
 * Calls forceReloadAllQueries() on every target DataClass — works on relation
 * classes (e.g. ActivityContactRelation) where update({}) was rejected by PISA.
 * For a single-record change, prefer reloadDataItem() instead.
 *
 * For workflows that require polling before invalidation (e.g. waiting for a
 * background job to complete), use waitFor() from src/utils/waitFor.ts alongside
 * this function — poll the job to completion, then call this to refresh caches.
 */
export function invalidateViaRegistry(entityType: DataClassName): void {
  for (const name of getInvalidationTargets(entityType)) {
    getDataClass(name)?.forceReloadAllQueries()
  }
}

/**
 * Reloads a single DataItem from PISA, refreshing its cached field values.
 * Use when only one record changed and cross-class invalidation is not needed.
 */
export const reloadDataItem = (item: DataItem): Promise<void> =>
  item.forceReload()
